import type { AbilityBuilder } from "@casl/ability"

import type { AclContext, AclSubjectType, AppAbility } from "./types"


export type AclPolicy = {
  subject: Exclude<AclSubjectType, "all">
  define: (builder: AbilityBuilder<AppAbility>, ctx: AclContext) => void
}

const POLICIES_GLOBAL_KEY = Symbol.for("@rpcbase/db/acl/policiesBySubject")

const getGlobalPoliciesMap = (): Map<string, Set<AclPolicy["define"]>> => {
  const store = globalThis as unknown as Record<symbol, unknown>
  const existing = store[POLICIES_GLOBAL_KEY]
  if (existing instanceof Map) {
    return existing as Map<string, Set<AclPolicy["define"]>>
  }
  const created = new Map<string, Set<AclPolicy["define"]>>()
  store[POLICIES_GLOBAL_KEY] = created
  return created
}

const policiesBySubject = getGlobalPoliciesMap()

const isPolicy = (value: unknown): value is AclPolicy => {
  if (!value || typeof value !== "object") return false
  const maybe = value as Partial<AclPolicy>
  return typeof maybe.subject === "string" && typeof maybe.define === "function"
}

export const registerPolicy = (policy: AclPolicy): void => {
  const set = policiesBySubject.get(policy.subject) ?? new Set<AclPolicy["define"]>()
  set.add(policy.define)
  policiesBySubject.set(policy.subject, set)
}

export const registerPoliciesFromModules = (modules: Record<string, unknown>): void => {
  for (const [exportName, value] of Object.entries(modules)) {
    if (!isPolicy(value)) continue

    if (!exportName.endsWith("Policy")) {
      throw new Error(
        `Invalid policy export name "${exportName}". Policies must be exported as "<ModelName>Policy".`,
      )
    }

    const expectedSubject = exportName.slice(0, -("Policy".length))
    if (value.subject !== expectedSubject) {
      throw new Error(
        `Invalid policy "${exportName}": expected subject "${expectedSubject}", got "${value.subject}".`,
      )
    }

    registerPolicy(value)
  }
}

export const getRegisteredPolicies = (): Array<AclPolicy["define"]> => {
  const out: Array<AclPolicy["define"]> = []
  for (const set of policiesBySubject.values()) {
    for (const fn of set) out.push(fn)
  }
  return out
}

export const hasRegisteredPolicy = (subject: string): boolean => {
  return policiesBySubject.has(subject)
}
